import React, { useMemo } from "react";
import { UniversalEntityPage } from "@/components/shared/UniversalEntityPage";
import { useContactHooks } from "@/lib/hooks/api/shared/graphql-hooks";
import AutoImportModal from "@/components/shared/modals/AutoImportModal";
import { getEntityTableConfig } from "@/lib/entity-registry";
import type { Contacts } from "@/src/gql/graphql";

const ENHANCED_CONTACTS_CONFIG = getEntityTableConfig<Contacts>("contacts");
import { useEntityContextOptional } from "@/components/shared/UniversalDetailPage/contexts/EntityContextProvider";
import type { CompanyTabComponentProps } from "@/lib/types/company";

export const CompanyContacts: React.FC<CompanyTabComponentProps> = ({ companyId: propCompanyId }) => {
  // Create hooks for contacts
  const contactHooks = useContactHooks();

  // Try to get company_id from EntityContext (when used in a detail page)
  // Falls back to prop if context is not available
  const entityContext = useEntityContextOptional();
  const companyId = propCompanyId || entityContext?.entityData?.company_id;

  // Memoize custom components to prevent recreation
  // Note: We don't include contactHooks in dependencies because the hooks object
  // reference changes on every render, but the actual hook functions don't change.
  const customComponents = useMemo(
    () => ({
      importModal: (props: any) => (
        <AutoImportModal config={ENHANCED_CONTACTS_CONFIG} hooks={contactHooks} {...props} />
      ),
    }),
    []
  );

  // Custom filter to show only contacts for this company
  // This filter is applied via customFilters prop which gets merged into the GraphQL query
  // The filter is applied BEFORE the filter context, ensuring it's always active
  const customFilters = useMemo(
    () => ({
      company_id: companyId,
    }),
    [companyId]
  );

  // Without a company id the `company_id` filter is dropped by
  // `normalizeCustomFiltersToGraphQL` (it skips null/undefined), which would
  // render EVERY contact in the tenant under a company heading. Show an empty
  // state instead — same guard the contracts tabs use.
  if (!companyId) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-card-foreground mb-2">No company</h3>
          <p className="text-gray-600 dark:text-gray-400">
            This record is not linked to a company, so there are no contacts to show.
          </p>
        </div>
      </div>
    );
  }

  return (
    <UniversalEntityPage
      entityName="contacts"
      config={ENHANCED_CONTACTS_CONFIG}
      hooks={contactHooks}
      filterPreset="all"
      title="Company Contacts"
      description={"View and manage contacts for this company"}
      storageKey="company-contacts-column-preferences"
      customComponents={customComponents}
      customFilters={customFilters}
      showHeader={false} // Hide header since it's in a tab
    />
  );
};
